import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Input,
  Badge,
  InputGroup,
  InputLeftElement,
  IconButton,
  Text,
  Icon,
  useBreakpointValue,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Container,
  Divider,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  useToast
} from '@chakra-ui/react';
import {
  SearchIcon,
  BellIcon,
  SettingsIcon,
  AtSignIcon,
  CalendarIcon,
  InfoIcon,
  ChevronDownIcon,
  ViewIcon,
  EditIcon,
  DeleteIcon
} from '@chakra-ui/icons';
import {
  FiUsers,
  FiBookOpen,
  FiVideo,
  FiCalendar,
  FiTrendingUp,
  FiActivity,
  FiBarChart,
  FiPieChart
} from 'react-icons/fi';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { motion } from 'framer-motion';
import axiosInstance from '../services/axiosInstance';
import SurveyTable from '../shared/SurveyTable';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const DashboardContent = () => {
  const toast = useToast();

  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [coursesData, setCoursesData] = useState({});
  const [usersData, setUsersData] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const [barData, setBarData] = useState({
    labels: ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
    datasets: [
      {
        label: "الأحداث حسب اليوم",
        backgroundColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 2,
        borderRadius: 8,
        data: [],
      },
    ],
  });

  const [pieData, setPieData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          primaryColor,
          secondaryColor,
          '#34495e',
          '#f5d547',
          '#d4b016',
          '#1a252f'
        ],
        borderWidth: 0,
      },
    ],
  });

  const [lineData, setLineData] = useState({
    labels: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو'],
    datasets: [
      {
        label: 'المستخدمين الجدد',
        data: [12, 19, 3, 5, 2, 3],
        borderColor: primaryColor,
        backgroundColor: `${primaryColor}20`,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'الدورات المكتملة',
        data: [2, 3, 20, 5, 1, 4],
        borderColor: secondaryColor,
        backgroundColor: `${secondaryColor}20`,
        tension: 0.4,
        fill: true,
      }
    ],
  });

  useEffect(() => {

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [barChartData, pieChartData, coursesData, usersResponse] = await Promise.all([
          axiosInstance.get('/dashboard/eventsPerDay'),
          axiosInstance.get('/dashboard/coursesByVideos'),
          axiosInstance.get('/statistics'),
          axiosInstance.get('/users'),
        ]);

        setCoursesData(coursesData.data);
        setUsersData({
          totalUsers: usersResponse.data.length,
          activeUsers: usersResponse.data.filter(user => user.lastLogin > Date.now() - 7 * 24 * 60 * 60 * 1000).length,
          adminUsers: usersResponse.data.filter(user => user.isAdmin).length
        });

        setBarData((prevData) => ({
          ...prevData,
          datasets: [
            {
              ...prevData.datasets[0],
              data: Array.isArray(barChartData.data) ? barChartData.data : [5, 8, 12, 6, 9, 4, 7],
            },
          ],
        }));

        const pieLabels = pieChartData.data.labels || ['الرياضيات', 'الفيزياء', 'الكيمياء', 'العلوم'];
        const pieDataValues = pieChartData.data.data || [25, 30, 20, 25];

        setPieData({
          labels: pieLabels,
          datasets: [
            {
              data: pieDataValues,
              backgroundColor: [
                primaryColor,
                secondaryColor,
                '#34495e',
                '#f5d547',
                '#d4b016',
                '#1a252f'
              ],
              borderWidth: 0,
            },
          ],
        });

        // Mock recent activity data
        setRecentActivity([
          { id: 1, action: 'تم إنشاء دورة جديدة', user: 'أحمد محمد', time: 'منذ 5 دقائق', type: 'course' },
          { id: 2, action: 'تسجيل مستخدم جديد', user: 'فاطمة علي', time: 'منذ 15 دقيقة', type: 'user' },
          { id: 3, action: 'تم رفع فيديو جديد', user: 'محمد حسن', time: 'منذ 30 دقيقة', type: 'video' },
          { id: 4, action: 'تم إنشاء حدث جديد', user: 'سارة أحمد', time: 'منذ ساعة', type: 'event' },
        ]);

      } catch (error) {
        console.error('خطأ أثناء جلب بيانات لوحة التحكم:', error);
        toast({
          title: 'خطأ في تحميل البيانات',
          description: 'حدث خطأ أثناء تحميل بيانات لوحة التحكم',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [toast]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: 'Cairo, sans-serif',
            size: 12
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(44, 62, 80, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: primaryColor,
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: {
          family: 'Cairo, sans-serif'
        },
        bodyFont: {
          family: 'Cairo, sans-serif'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: 'Cairo, sans-serif'
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            family: 'Cairo, sans-serif'
          }
        }
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: 'Cairo, sans-serif',
            size: 12
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(44, 62, 80, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: primaryColor,
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (tooltipItem) => {
            const label = tooltipItem.label || '';
            const value = tooltipItem.raw || '';
            return `${label}: ${value} فيديوهات`;
          },
        },
      },
    },
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'course': return FiBookOpen;
      case 'user': return FiUsers;
      case 'video': return FiVideo;
      case 'event': return FiCalendar;
      default: return FiActivity;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'course': return 'blue';
      case 'user': return 'green';
      case 'video': return 'purple';
      case 'event': return 'orange';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <VStack spacing={4}>
          <Box className="spinner" />
          <Text color={textColor} fontFamily="Cairo, sans-serif">جاري تحميل لوحة التحكم...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      {/* Header */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb={8}
          flexDirection={{ base: 'column', md: 'row' }}
          bg={cardBg}
          p={6}
          borderRadius="xl"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack align="start" spacing={2}>
            <Heading
              as="h1"
              size="xl"
              color={secondaryColor}
              fontFamily="Cairo, sans-serif"
              fontWeight="700"
            >
              لوحة التحكم الإدارية
            </Heading>
            <Text color="gray.600" fontSize="md">
              مرحباً بك في نظام إدارة المنصة التعليمية
            </Text>
          </VStack>

          <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="البحث في النظام..."
                bg="gray.50"
                border="2px solid"
                borderColor="gray.200"
                _focus={{
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 1px ${primaryColor}`
                }}
                borderRadius="lg"
              />
            </InputGroup>
            <HStack spacing={2}>
              <IconButton
                icon={<BellIcon />}
                aria-label="الإشعارات"
                bg="white"
                border="2px solid"
                borderColor="gray.200"
                _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                borderRadius="lg"
              />
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg="white" border="2px solid" borderColor="gray.200">
                  <Avatar size="sm" name="المدير" bg={primaryColor} color={secondaryColor} />
                </MenuButton>
                <MenuList>
                  <MenuItem icon={<ViewIcon />}>عرض الملف الشخصي</MenuItem>
                  <MenuItem icon={<SettingsIcon />}>الإعدادات</MenuItem>
                  <MenuItem icon={<EditIcon />}>تعديل البيانات</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </HStack>
        </Flex>
      </motion.div>

      {/* Statistics Cards */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <CardBody p={6}>
                <Stat>
                  <Flex align="center" justify="space-between">
                    <Box>
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        إجمالي المستخدمين
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {usersData.totalUsers || 0}
                      </StatNumber>
                      <StatHelpText color="green.500" fontSize="sm">
                        <StatArrow type="increase" />
                        12% هذا الشهر
                      </StatHelpText>
                    </Box>
                    <Box
                      bg={`${primaryColor}20`}
                      p={3}
                      borderRadius="lg"
                    >
                      <Icon as={FiUsers} color={primaryColor} boxSize={6} />
                    </Box>
                  </Flex>
                </Stat>
              </CardBody>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <CardBody p={6}>
                <Stat>
                  <Flex align="center" justify="space-between">
                    <Box>
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        عدد الدورات
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {coursesData.totalCourses || 0}
                      </StatNumber>
                      <StatHelpText color="green.500" fontSize="sm">
                        <StatArrow type="increase" />
                        8% هذا الشهر
                      </StatHelpText>
                    </Box>
                    <Box
                      bg={`${secondaryColor}20`}
                      p={3}
                      borderRadius="lg"
                    >
                      <Icon as={FiBookOpen} color={secondaryColor} boxSize={6} />
                    </Box>
                  </Flex>
                </Stat>
              </CardBody>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <CardBody p={6}>
                <Stat>
                  <Flex align="center" justify="space-between">
                    <Box>
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        عدد الفيديوهات
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {coursesData.totalVideos || 0}
                      </StatNumber>
                      <StatHelpText color="blue.500" fontSize="sm">
                        <StatArrow type="increase" />
                        15% هذا الشهر
                      </StatHelpText>
                    </Box>
                    <Box
                      bg="blue.100"
                      p={3}
                      borderRadius="lg"
                    >
                      <Icon as={FiVideo} color="blue.500" boxSize={6} />
                    </Box>
                  </Flex>
                </Stat>
              </CardBody>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <CardBody p={6}>
                <Stat>
                  <Flex align="center" justify="space-between">
                    <Box>
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        عدد الأحداث
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {coursesData.totalEvents || 0}
                      </StatNumber>
                      <StatHelpText color="orange.500" fontSize="sm">
                        <StatArrow type="increase" />
                        5% هذا الشهر
                      </StatHelpText>
                    </Box>
                    <Box
                      bg="orange.100"
                      p={3}
                      borderRadius="lg"
                    >
                      <Icon as={FiCalendar} color="orange.500" boxSize={6} />
                    </Box>
                  </Flex>
                </Stat>
              </CardBody>
            </Card>
          </motion.div>
        </SimpleGrid>
      </motion.div>

      {/* Charts and Activity */}
      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} mb={8}>
        {/* Bar Chart */}
        <motion.div variants={fadeIn}>
          <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
            <CardHeader pb={2}>
              <Flex align="center" justify="space-between">
                <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                  الأحداث حسب اليوم
                </Heading>
                <Icon as={FiBarChart} color={primaryColor} boxSize={5} />
              </Flex>
            </CardHeader>
            <CardBody pt={2}>
              <Box h="300px">
                <Bar data={barData} options={chartOptions} />
              </Box>
            </CardBody>
          </Card>
        </motion.div>

        {/* Pie Chart */}
        <motion.div variants={fadeIn}>
          <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
            <CardHeader pb={2}>
              <Flex align="center" justify="space-between">
                <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                  الدورات حسب المواد
                </Heading>
                <Icon as={FiPieChart} color={primaryColor} boxSize={5} />
              </Flex>
            </CardHeader>
            <CardBody pt={2}>
              <Box h="300px">
                <Pie data={pieData} options={pieOptions} />
              </Box>
            </CardBody>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div variants={fadeIn}>
          <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
            <CardHeader pb={2}>
              <Flex align="center" justify="space-between">
                <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                  النشاط الأخير
                </Heading>
                <Icon as={FiActivity} color={primaryColor} boxSize={5} />
              </Flex>
            </CardHeader>
            <CardBody pt={2}>
              <VStack spacing={4} align="stretch" maxH="300px" overflowY="auto">
                {recentActivity.map((activity) => (
                  <Flex key={activity.id} align="center" p={3} bg="gray.50" borderRadius="lg">
                    <Box
                      bg={`${getActivityColor(activity.type)}.100`}
                      p={2}
                      borderRadius="lg"
                      mr={3}
                    >
                      <Icon
                        as={getActivityIcon(activity.type)}
                        color={`${getActivityColor(activity.type)}.500`}
                        boxSize={4}
                      />
                    </Box>
                    <Box flex="1">
                      <Text fontSize="sm" fontWeight="500" color={textColor}>
                        {activity.action}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        بواسطة {activity.user} • {activity.time}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </CardBody>
          </Card>
        </motion.div>
      </SimpleGrid>

      {/* Line Chart */}
      <motion.div variants={fadeIn}>
        <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden" mb={8}>
          <CardHeader pb={2}>
            <Flex align="center" justify="space-between">
              <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                إحصائيات النمو الشهرية
              </Heading>
              <Icon as={FiTrendingUp} color={primaryColor} boxSize={5} />
            </Flex>
          </CardHeader>
          <CardBody pt={2}>
            <Box h="400px">
              <Line data={lineData} options={chartOptions} />
            </Box>
          </CardBody>
        </Card>
      </motion.div>

      {/* Survey Table */}
      <motion.div variants={fadeIn}>
        <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
          <CardHeader>
            <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
              إدارة الاستطلاعات
            </Heading>
          </CardHeader>
          <CardBody>
            <SurveyTable />
          </CardBody>
        </Card>
      </motion.div>
    </Box>
  );
};

const AdminDashboard = () => (
  <DashboardContent />
);

export default AdminDashboard;

